import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import {
  CALENDAR_MONTH_NAMES,
  CALENDAR_PICKER_EDGE_PADDING_PX,
  CALENDAR_PICKER_ITEM_HEIGHT_PX,
  buildCalendarPickerYears,
  calendarPickerItemTop,
  calendarPickerMonthItems,
  calendarPickerSnapIndex,
} from "../../utils/calendarMonthYearPicker";

function pickerItemClass(selected) {
  return selected
    ? "flex w-full items-center justify-center text-center text-xs font-semibold text-gray-900 transition-colors"
    : "flex w-full items-center justify-center text-center text-xs font-normal text-gray-400 transition-colors hover:text-gray-600";
}

export function CalendarPickerWheelColumn({ items, selectedValue, onSelect, getLabel, getValue }) {
  const listRef = useRef(null);
  const scrollEndTimerRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);

  const scrollToIndex = useCallback(
    (index, behavior = "auto") => {
      const list = listRef.current;
      if (!list) return;
      const clampedIndex = Math.max(0, Math.min(items.length - 1, index));
      isProgrammaticScrollRef.current = true;
      const top = calendarPickerItemTop(clampedIndex);
      if (typeof list.scrollTo === "function") {
        list.scrollTo({ top, behavior });
      } else {
        list.scrollTop = top;
      }
      window.requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    },
    [items.length]
  );

  const scrollToValue = useCallback(
    (value, behavior = "auto") => {
      const index = items.findIndex((item) => getValue(item) === value);
      if (index >= 0) scrollToIndex(index, behavior);
    },
    [items, getValue, scrollToIndex]
  );

  const snapToNearest = useCallback(() => {
    const list = listRef.current;
    if (!list || isProgrammaticScrollRef.current) return;

    const clampedIndex = calendarPickerSnapIndex(list.scrollTop, items.length);
    const targetTop = calendarPickerItemTop(clampedIndex);
    if (Math.abs(list.scrollTop - targetTop) > 1) {
      scrollToIndex(clampedIndex, "smooth");
    }

    const landedValue = getValue(items[clampedIndex]);
    if (landedValue !== selectedValue) {
      onSelect(landedValue);
    }
  }, [items, getValue, onSelect, scrollToIndex, selectedValue]);

  useLayoutEffect(() => {
    scrollToValue(selectedValue, "auto");
  }, [selectedValue, scrollToValue]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;

    const handleScrollEnd = () => snapToNearest();
    list.addEventListener("scrollend", handleScrollEnd);
    return () => {
      list.removeEventListener("scrollend", handleScrollEnd);
      if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
    };
  }, [snapToNearest]);

  const handleScroll = () => {
    if (isProgrammaticScrollRef.current) return;
    if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = setTimeout(snapToNearest, 120);
  };

  return (
    <div
      ref={listRef}
      onScroll={handleScroll}
      className="h-36 flex-1 overflow-y-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      style={{
        paddingTop: CALENDAR_PICKER_EDGE_PADDING_PX,
        paddingBottom: CALENDAR_PICKER_EDGE_PADDING_PX,
      }}
    >
      {items.map((item) => {
        const value = getValue(item);
        const selected = value === selectedValue;
        return (
          <button
            key={String(value)}
            type="button"
            style={{ height: CALENDAR_PICKER_ITEM_HEIGHT_PX }}
            className={pickerItemClass(selected)}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onSelect(value);
              scrollToValue(value, "smooth");
            }}
          >
            {getLabel(item)}
          </button>
        );
      })}
    </div>
  );
}

export default function CalendarMonthYearPicker({
  monthDate,
  calendarViewSpan = 1,
  rangeLabel,
  onNavigate,
}) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const years = useMemo(() => buildCalendarPickerYears(year), [year]);
  const monthItems = useMemo(() => calendarPickerMonthItems(), []);
  const label = calendarViewSpan > 1 ? rangeLabel : `${CALENDAR_MONTH_NAMES[month]} ${year}`;

  return (
    <Menu as="div" className="relative flex-auto text-center">
      <Menu.Button className="inline-flex w-full items-center justify-center gap-1 rounded-md px-2 py-1 text-sm font-semibold text-gray-900 hover:bg-gray-50 hover:text-indigo-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
        <span>{label}</span>
        <ChevronDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
      </Menu.Button>
      <Menu.Items className="absolute left-1/2 z-30 mt-1.5 w-60 -translate-x-1/2 origin-top rounded-lg bg-white p-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
        <div className="relative overflow-hidden rounded-md">
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-white to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-6 bg-gradient-to-t from-white to-transparent" />
          <div
            className="pointer-events-none absolute inset-x-1.5 top-1/2 z-10 h-7 -translate-y-1/2 rounded border-y border-gray-200 bg-gray-50/40"
            aria-hidden="true"
          />
          <Menu.Item as="div" className="relative flex divide-x divide-gray-100">
            <CalendarPickerWheelColumn
              items={monthItems}
              selectedValue={month}
              onSelect={(monthIndex) => onNavigate(year, monthIndex)}
              getLabel={(item) => item.name}
              getValue={(item) => item.index}
            />
            <CalendarPickerWheelColumn
              items={years}
              selectedValue={year}
              onSelect={(selectedYear) => onNavigate(selectedYear, month)}
              getLabel={(optionYear) => String(optionYear)}
              getValue={(optionYear) => optionYear}
            />
          </Menu.Item>
        </div>
      </Menu.Items>
    </Menu>
  );
}
